import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from '@/lib/api';
import { toast } from "@/lib/toast-with-sound";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Receipt, Wallet } from "lucide-react";
import { TranslatableField, getTranslatedValue } from "@/components/TranslatableField";
import { FIELD_LIMITS } from "@/constants/fieldLimits";
import { PlanLimitBanner } from "@/components/PlanLimitBanner";

const CURRENCY_SYMBOLS = {
  EUR: "€", GBP: "£", USD: "$", CHF: "CHF", BRL: "R$",
  MAD: "MAD", TRY: "₺", PLN: "zł", CZK: "Kč",
  SEK: "kr", NOK: "kr", DKK: "kr", RON: "lei", HUF: "Ft",
};
const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || "€";

/** Format amount for display: 1.250 € (pt-PT style, integer) */
const formatAmount = (amount, currency = "EUR") => {
  const num = parseInt(Number(amount), 10) || 0;
  const thousands = String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${thousands} ${getCurrencySymbol(currency)}`;
};

// ─── Amount Input: integer, non-negative, max 6 digits (0–999999) ───
function AmountInput({ value, onChange, costId }) {
  const toDisplay = (num) => {
    const n = parseInt(num, 10);
    return n > 0 ? String(n) : "";
  };
  const [text, setText] = useState(() => toDisplay(value));
  const committed = useRef(value);

  useEffect(() => {
    if (value !== committed.current) {
      setText(toDisplay(value));
      committed.current = value;
    }
  }, [value]);

  return (
    <Input
      type="text" inputMode="numeric" placeholder="0"
      value={text}
      onChange={(e) => {
        const v = e.target.value;
        if (v !== "" && !/^\d{0,6}$/.test(v)) return;
        setText(v);
      }}
      onBlur={() => {
        const num = parseInt(text, 10);
        const final = isNaN(num) || num < 0 ? 0 : Math.min(num, 999999);
        setText(final > 0 ? String(final) : "");
        committed.current = final;
        onChange(final);
      }}
      className="form-input font-mono"
      data-testid={`cost-amount-${costId}`}
    />
  );
}

// ─── Individual Cost Item (inline editable, auto-save) ───
function CostItem({ cost, tripId, categories, currency, canEdit, currentLang, onDelete, onUpdated, setSaveStatus }) {
  const { t } = useTranslation("common");
  const [localData, setLocalData] = useState({
    category: cost.category || "",
    amount: cost.amount || 0,
    description: normalizeDesc(cost.description),
    date: cost.date || "",
  });
  const saveTimer = useRef(null);
  const descTranslateTimer = useRef(null);
  const lastDescHash = useRef("");
  const [translating, setTranslating] = useState(false);

  // Sync from parent when cost changes externally (e.g. after translation refresh)
  useEffect(() => {
    setLocalData({
      category: cost.category || "",
      amount: cost.amount || 0,
      description: normalizeDesc(cost.description),
      date: cost.date || "",
    });
  }, [cost.category, cost.amount, cost.date, cost.cost_id]);

  function normalizeDesc(desc) {
    if (!desc) return { pt: "", en: "", es: "", fr: "" };
    if (typeof desc === "string") return { pt: desc, en: "", es: "", fr: "" };
    return { pt: "", en: "", es: "", fr: "", ...desc };
  }

  const textHash = (s) => {
    if (!s) return "";
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xFFFFFFFF;
    return (h >>> 0).toString(16);
  };

  // Debounced auto-save via PUT
  const scheduleSave = useCallback((data) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveStatus?.("saving");
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await api.put(`/api/trips/${tripId}/costs/${cost.cost_id}`, {
          category: data.category,
          amount: parseFloat(data.amount) || 0,
          description: data.description,
          date: data.date || null,
        });
        setSaveStatus?.("saved");
        setTimeout(() => setSaveStatus?.("idle"), 2500);
        if (res.data?.cost) onUpdated?.(res.data.cost);
      } catch (err) {
        console.error("Cost auto-save error:", err);
        setSaveStatus?.("error");
        setTimeout(() => setSaveStatus?.("idle"), 3000);
      }
    }, 800);
  }, [tripId, cost.cost_id, onUpdated, setSaveStatus]);

  // Debounced description translation
  const scheduleTranslation = useCallback(async (desc) => {
    const srcLang = desc?._src_lang || currentLang;
    const srcText = desc?.[srcLang]?.trim();
    if (!srcText) return;
    const hash = textHash(srcText);
    if (hash === lastDescHash.current) return;
    lastDescHash.current = hash;
    setTranslating(true);
    try {
      const res = await api.post("/api/translate", {
        text: srcText, source_lang: srcLang,
        target_langs: ["pt", "en", "es", "fr"].filter(l => l !== srcLang),
      });
      if (res.data?.translations) {
        const merged = { ...desc, ...res.data.translations };
        delete merged._src_lang;
        delete merged._src_hash;
        setLocalData(prev => {
          const next = { ...prev, description: merged };
          scheduleSave(next);
          return next;
        });
      } else {
        // No translations returned — save untranslated text
        setLocalData(prev => { scheduleSave(prev); return prev; });
      }
    } catch {
      // Translation failed — still save current text
      setLocalData(prev => { scheduleSave(prev); return prev; });
    }
    finally { setTranslating(false); }
  }, [currentLang, scheduleSave]);

  const updateField = (field, value) => {
    setLocalData(prev => {
      const next = { ...prev, [field]: value };
      scheduleSave(next);
      return next;
    });
  };

  const handleDescriptionChange = (val) => {
    const srcLang = val?._src_lang || currentLang;
    const srcText = val?.[srcLang]?.trim();

    setLocalData(prev => ({ ...prev, description: val }));

    // Clear pending timers (both save and translate share the 2s debounce)
    if (descTranslateTimer.current) clearTimeout(descTranslateTimer.current);
    if (saveTimer.current) clearTimeout(saveTimer.current);

    // If source text is cleared, reset hash
    if (!srcText) {
      lastDescHash.current = "";
    }

    // 2-second debounce for all description changes (save indicator shown only when actual save starts via scheduleSave)
    descTranslateTimer.current = setTimeout(() => {
      if (srcText && textHash(srcText) !== lastDescHash.current) {
        scheduleTranslation(val);
      } else {
        // Empty or unchanged text → save directly using latest state
        setLocalData(prev => {
          scheduleSave(prev);
          return prev;
        });
      }
    }, 2000);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (descTranslateTimer.current) clearTimeout(descTranslateTimer.current);
    };
  }, []);

  const getCategoryName = (key) => {
    const cat = categories.find(c => c.key === key);
    if (cat?.name) return getTranslatedValue(cat.name, currentLang);
    return key;
  };

  if (!canEdit) {
    // Read-only view
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 px-3 py-2 text-sm" data-testid={`cost-item-${cost.cost_id}`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {cost.date && <span className="text-xs text-muted-foreground font-mono shrink-0">{formatDateShort(cost.date)}</span>}
          <span className="text-muted-foreground break-words">{getTranslatedValue(cost.description, currentLang) || "—"}</span>
        </div>
        <span className="font-mono font-medium shrink-0">{formatAmount(cost.amount, currency)}</span>
      </div>
    );
  }

  // Editable inline view
  return (
    <div className="space-y-4" data-testid={`cost-item-${cost.cost_id}`}>
      {/* Item header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <span className="text-sm font-semibold text-primary uppercase tracking-wide flex items-center gap-2 min-w-0">
          <Receipt className="h-4 w-4 shrink-0" />
          <span className="break-words">{getCategoryName(localData.category) || t("trip_detail.costs_new", "Novo custo")}</span>
        </span>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-cost-${cost.cost_id}`}>
              <Trash2 className="h-3 w-3 mr-1" />
              {t("actions.delete")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("trip_detail.costs_delete")}</AlertDialogTitle>
              <AlertDialogDescription>{t("trip_detail.costs_delete_confirm")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("actions.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(cost.cost_id)}>{t("actions.delete")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {/* Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">{t("trip_detail.costs_category")} <span className="text-destructive">*</span></Label>
          <Select value={localData.category} onValueChange={(v) => updateField("category", v)}>
            <SelectTrigger className="form-input" data-testid={`cost-category-${cost.cost_id}`}>
              <SelectValue placeholder={t("trip_detail.costs_category")} />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.key || cat.category_id} value={cat.key}>
                  {getTranslatedValue(cat.name, currentLang)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">{t("trip_detail.costs_amount")} ({getCurrencySymbol(currency)}) <span className="text-destructive">*</span></Label>
          <AmountInput
            value={localData.amount}
            onChange={(num) => updateField("amount", num)}
            costId={cost.cost_id}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">{t("trip_detail.costs_date")}</Label>
          <DatePicker
            value={localData.date ? new Date(localData.date + "T00:00:00") : null}
            onChange={(date) => {
              const iso = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` : "";
              updateField("date", iso);
            }}
            placeholder={t("trip_detail.costs_date")}
            data-testid={`cost-date-${cost.cost_id}`}
          />
        </div>
      </div>
      <div data-testid={`cost-desc-${cost.cost_id}`}>
        <TranslatableField
          id={`cost-desc-${cost.cost_id}`}
          label={t("trip_detail.costs_description")}
          value={localData.description}
          onChange={handleDescriptionChange}
          placeholder={t("trip_detail.costs_description")}
          maxLength={FIELD_LIMITS.COST_DESCRIPTION}
        />
      </div>
    </div>
  );
}

function formatDateShort(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
}

// ─── Main Costs Tab ───
export function TripCostsTab({ tripId, costs = [], canEdit = false, onCostsChange, currency = "EUR", setSaveStatus }) {
  const { t, i18n } = useTranslation("common");
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const [categories, setCategories] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/api/cost-categories").then(res => setCategories(res.data || [])).catch(() => {});
  }, []);

  const totalAmount = useMemo(() => costs.reduce((sum, c) => sum + (c.amount || 0), 0), [costs]);

  const hasInvalidCosts = useMemo(() =>
    costs.some(c => {
      // Required fields
      if (!c.category?.trim() || !c.amount || parseFloat(c.amount) <= 0) return true;
      // If primary language description has text, all translations must be filled
      const desc = c.description;
      if (desc && typeof desc === "object") {
        const primaryText = (desc.pt || "").trim();
        if (primaryText) {
          const allLangs = ["pt", "en", "es", "fr"];
          const missingTranslations = allLangs.some(l => !(desc[l] || "").trim());
          if (missingTranslations) return true;
        }
      }
      return false;
    }),
    [costs]
  );

  const handleAdd = async () => {
    setAdding(true);
    try {
      await api.post(`/api/trips/${tripId}/costs`, { category: "", amount: 0 });
      await onCostsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("trip_detail.costs_error"));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (costId) => {
    try {
      await api.delete(`/api/trips/${tripId}/costs/${costId}`);
      toast.success(t("trip_detail.costs_deleted"));
      await onCostsChange?.();
    } catch {
      toast.error(t("trip_detail.costs_error"));
    }
  };

  const handleCostUpdated = async () => {
    await onCostsChange?.();
  };

  // Empty state
  if (costs.length === 0 && !canEdit) {
    return (
      <Card className="bg-card/80 backdrop-blur-sm border-border rounded-xl overflow-hidden border-t-2 border-t-primary border-b-2 border-b-primary" data-testid="costs-tab-empty">
        <div className="bg-gradient-to-r from-primary/25 via-primary/15 to-transparent px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20 border border-primary/30">
              <Receipt className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">{t("trip_detail.costs_tab_title")}</h3>
              <p className="text-xs text-muted-foreground">{t("trip_detail.costs_tab_subtitle", "Custos suportados durante o passeio.")}</p>
            </div>
          </div>
        </div>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <Wallet className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t("trip_detail.costs_no_data")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/80 backdrop-blur-sm border-border rounded-xl overflow-hidden border-t-2 border-t-primary border-b-2 border-b-primary" data-testid="costs-tab">
      <div className="bg-gradient-to-r from-primary/25 via-primary/15 to-transparent px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/20 border border-primary/30">
            <Receipt className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t("trip_detail.costs_tab_title")}</h3>
              <p className="text-xs text-muted-foreground">{t("trip_detail.costs_tab_subtitle", "Custos suportados durante o passeio.")}</p>
          </div>
        </div>
      </div>
      <CardContent className="pt-4 space-y-4">
        {/* Summary */}
        {costs.length > 0 && (
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg" data-testid="costs-summary">
            <div className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-primary shrink-0" />
              <span className="text-xs sm:text-sm font-medium">{t("trip_detail.costs_summary")}</span>
              <span className="text-xs text-muted-foreground">({costs.length})</span>
            </div>
            <div className="text-base sm:text-lg font-bold font-mono text-primary mt-1" data-testid="costs-total">
              {formatAmount(totalAmount, currency)}
            </div>
          </div>
        )}

        {/* Divider between summary and first cost block */}
        {costs.length > 0 && (
          <div className="border-t-2 border-primary my-4" />
        )}

        {/* Inline cost items */}
        {costs.length > 0 && (
          <div className="space-y-4">
            {costs.map((cost, idx) => (
              <div key={cost.cost_id}>
                {idx > 0 && (
                  <div className="border-t-2 border-primary my-4" />
                )}
                <CostItem
                  cost={cost}
                  tripId={tripId}
                  categories={categories}
                  currency={currency}
                  canEdit={canEdit}
                  currentLang={currentLang}
                  onDelete={handleDelete}
                  onUpdated={handleCostUpdated}
                  setSaveStatus={setSaveStatus}
                />
              </div>
            ))}
          </div>
        )}

        {/* Add button */}
        {canEdit && (
          <>
            <PlanLimitBanner feature="max_costs_per_trip" current={costs.length} className="mb-2" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAdd}
              disabled={adding || hasInvalidCosts}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="add-cost-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t("trip_detail.costs_add")}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
